import React, { useState, useRef, useEffect } from "react";
import { Search, ChevronDown, ChevronUp, X } from "lucide-react";
import "./DropDown.css"; // Keep your existing CSS file for additional styles

const DropDown = ({
  options = [],
  onChange,
  placeholder = "Select an option",
  className = "",
  icon,
  search = false,
  label,
  name,
  required = false,
  register,
  errors,
  defaultValue = "",
  labelClass = "font-bold text-bases",
  secondLabel,
  hideClear = false
}) => {
  // Find the default option based on defaultValue prop
  const defaultOption = options.find((option) => option.value === defaultValue);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState(defaultOption || null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Handle react-hook-form integration if register is provided
  const registerProps = register ? {
    ...register(name, {
      required,
      value: defaultValue,
    })
  } : {};

  // Update selected option when defaultValue changes
  useEffect(() => {
    const option = options.find((option) => option.value === defaultValue);
    if (option) {
      setSelectedOption(option);
    }
  }, [defaultValue, options]);

  // Filter options based on search term
  const filteredOptions = search
    ? options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && search && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, search]);

  // Handle option selection
  const handleSelectOption = (option) => {
    setSelectedOption(option);
    setIsOpen(false);
    setSearchTerm("");

    // Handle both react-hook-form and regular onChange
    if (register && name) {
      const event = {
        target: {
          name,
          value: option.value,
        },
      };
      registerProps.onChange(event);
    }
    
    if (onChange) {
      onChange({ target: { value: option.value } });
    }
  };

  // Toggle dropdown open/closed
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen && search) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  };

  // Clear selection
  const clearSelection = (e) => {
    e.stopPropagation();
    setSelectedOption(null);
    setSearchTerm("");

    // Handle both react-hook-form and regular onChange
    if (register && name) {
      const event = {
        target: {
          name,
          value: "",
        },
      };
      registerProps.onChange(event);
    }
    
    if (onChange) {
      onChange({ target: { value: "" } });
    }
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {label && (
        <div className="flex items-center justify-between">
          <label className={labelClass} htmlFor={name}>
            {label}
            {required && <span className="text-red-500 text-xs ml-0.5">*</span>}
          </label>
          {secondLabel}
        </div>
      )}

      <div className="relative" ref={dropdownRef}>
        <div
          className={`flex items-center justify-between border rounded-lg px-4 py-3 cursor-pointer transition-all ${
            isOpen ? "ring-2 ring-blue-200 border-blue-300" : ""
          } ${
            errors?.[name]
              ? "border-red-500 focus:border-red-300 ring-red-200"
              : "border-gray-300 hover:border-gray-400"
          }`}
          onClick={toggleDropdown}
        >
          <div className="flex-1 flex items-center">
            {icon && <div className="mr-2">{icon}</div>}
            
            {isOpen && search ? (
              <div className="flex items-center w-full">
                <Search size={16} className="text-gray-400 mr-2" />
                <input
                  type="text"
                  className="w-full outline-none bg-transparent"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  ref={searchInputRef}
                />
              </div>
            ) : (
              <span className={`${!selectedOption && "text-gray-500"}`}>
                {selectedOption ? selectedOption.label : placeholder}
              </span>
            )}
          </div>
          
          <div className="flex items-center">
            {selectedOption && !isOpen && !hideClear && (
              <button
                type="button"
                onClick={clearSelection}
                className="p-1 hover:bg-gray-100 rounded-full mr-1"
              >
                <X size={16} className="text-gray-500" />
              </button>
            )}
            {isOpen ? (
              <ChevronUp size={18} className="text-gray-500" />
            ) : (
              <ChevronDown size={18} className="text-gray-500" />
            )}
          </div>
        </div>

        {isOpen && (
          <div className="absolute z-10 mt-1 w-full max-h-60 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={index}
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-50 ${
                    selectedOption?.value === option.value
                      ? "bg-blue-50 text-blue-600"
                      : ""
                  }`}
                  onClick={() => handleSelectOption(option)}
                >
                  {option.label}
                </div>
              ))
            ) : (
              <div className="px-4 py-2 text-gray-500 italic">
                No options found
              </div>
            )}
          </div>
        )}

        {/* Hidden input for react-hook-form if register is provided */}
        {register && (
          <input
            type="hidden"
            name={name}
            value={selectedOption?.value || ""}
            defaultValue={defaultValue}
            {...registerProps}
          />
        )}
      </div>

      {errors && errors[name] && (
        <p className="text-red-500 text-xs">{errors[name]?.message}</p>
      )}
    </div>
  );
};

export default DropDown;